import { useEffect, useState } from 'react';
import type { Asignacion, ListaInvitadosEvento } from '../../../api/ops/listas';
import { ErrorApi } from '../../../api/problema';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Deshacer } from '../../../componentes/Deshacer';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { usePermisos } from '../../../permisos/permisos';
import { Bloque, Botonera, Botones, Caja, Dato, FechaHora, Fila, Vacia } from '../comun';
import { aLocal, deLocal, useAvisarError, useCarga } from '../uso';
import {
    acompanantes,
    ajustesDeFormulario,
    apuntadosLista,
    colorDe,
    contactoInvitado,
    cupoTexto,
    diaHora,
    enPuerta,
    erroresLista,
    ESTADO_LISTA,
    estadoInvitado,
    formularioDeLista,
    nombreRrpp,
    opsDe,
    PERMISOS,
    RUTA_CANALES_DE_ENVIO,
    type FormularioLista,
} from '../textos';
import type { ContextoListas } from './ApartadoListas';
import { FormLista } from './FormLista';

/**
 * Ficha de una lista (10.9): Invitados · Ajustes · RRPP. Ajustes con `updateGuestList` e `If-Match` (412: vuelve a leer);
 * cerrar con «Deshacer» de 5 s (la petición sale al acabar, G159), reabrir con un cierre nuevo y cancelar con motivo y
 * ventana (avisa a los invitados). Los cupos de los RRPP se cambian en RRPP › En el evento.
 */
export function FichaLista({ ctx, listaId, pestanaInicial, asignaciones }: { ctx: ContextoListas; listaId: string; pestanaInicial: number; asignaciones: readonly Asignacion[] }) {
    const { props, evento } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const envio = useEnvioRetrasado();
    const [pestana, setPestana] = useState(pestanaInicial);
    const [leida, setLeida] = useState<{ lista: ListaInvitadosEvento; etag: string } | null>(null);
    const [f, setF] = useState<FormularioLista | null>(null);
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [ventana, setVentana] = useState<'reabrir' | 'cancelar' | null>(null);
    const [motivo, setMotivo] = useState('');
    const [cierre, setCierre] = useState('');
    const [vuelta, setVuelta] = useState(0);

    useEffect(() => {
        let vivo = true;
        ops.getGuestList(negocio, listaId)
            .then((r) => {
                if (vivo) {
                    setLeida({ lista: r.datos, etag: r.version ?? '' });
                    setF(formularioDeLista(r.datos, aLocal));
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    avisarError(e, 'No se ha podido abrir la lista.');
                    ctx.abrir(null);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [negocio, listaId, vuelta]);

    const invitados = useCarga(pestana === 0 ? () => ops.listGuests(negocio, { guest_list_id: listaId, limit: 25 }) : null, [negocio, listaId, pestana, vuelta]);

    const l = leida?.lista;
    const terminado = evento?.status === 'ended';
    const gestionar = permisos.puede(PERMISOS.listasGestionar) && !soloLectura && l !== undefined && l.status !== 'cancelled' && !terminado;
    const apuntar = permisos.puede(PERMISOS.invitadosGestionar) && !soloLectura && l?.status === 'open';
    const errores = probado && f !== null ? erroresLista(f) : {};
    const moneda = l?.door_price?.currency ?? 'EUR';

    const guardar = () => {
        if (f === null || leida === null) {
            return;
        }
        if (Object.keys(erroresLista(f)).length > 0) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        ops.updateGuestList(negocio, listaId, { ...ajustesDeFormulario(f, moneda), door_cutoff_at: deLocal(f.horaLimite), closes_at: deLocal(f.cierre) }, leida.etag)
            .then((r) => {
                setLeida({ lista: r.datos, etag: r.version ?? leida.etag });
                setProbado(false);
                avisar({ tipo: 'ok', texto: 'Ajustes guardados.' });
                ctx.recargar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 412) {
                    avisar({ tipo: 'error', texto: 'Alguien ha cambiado la lista. La hemos vuelto a leer.' });
                    setVuelta((v) => v + 1);
                } else {
                    avisarError(e);
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const cerrar = () => {
        envio.programar({
            texto: 'Lista cerrada: nadie más se puede apuntar. Se aplica en 5 segundos.',
            donde: 'ajustes',
            enviar: () => {
                ops.closeGuestList(negocio, listaId)
                    .then(() => { setVuelta((v) => v + 1); ctx.recargar(); })
                    .catch((e: unknown) => { avisarError(e, 'No se ha podido cerrar la lista.'); });
            },
        });
    };

    const confirmar = () => {
        if (ventana === 'cancelar') {
            if (motivo.trim() === '') {
                setProbado(true);
                return;
            }
            setOcupado(true);
            ops.cancelGuestList(negocio, listaId, motivo.trim())
                .then((r) => {
                    avisar({ tipo: 'ok', texto: `«${r.name ?? ''}» cancelada.` });
                    setVentana(null);
                    ctx.recargar();
                    ctx.abrir(null);
                })
                .catch((e: unknown) => { avisarError(e); })
                .finally(() => { setOcupado(false); });
        } else if (ventana === 'reabrir') {
            const iso = deLocal(cierre);
            if (iso === null) {
                setProbado(true);
                return;
            }
            setOcupado(true);
            ops.reopenGuestList(negocio, listaId, iso)
                .then(() => {
                    avisar({ tipo: 'ok', texto: `Lista reabierta hasta el ${diaHora(iso)}.` });
                    setVentana(null);
                    setVuelta((v) => v + 1);
                    ctx.recargar();
                })
                .catch((e: unknown) => { avisarError(e); })
                .finally(() => { setOcupado(false); });
        }
    };

    const chip = l === undefined || l.status === 'open' ? undefined : ESTADO_LISTA[l.status];
    const accion = pestana === 0 && apuntar ? 'Apuntar invitado' : pestana === 1 && gestionar ? 'Guardar' : '';
    const conCupo = asignaciones.filter((a) => a.status === 'active' && a.lists.some((x) => x.guest_list_id === listaId));

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { ctx.abrir(null); }}
                movil={movil}
                tamano="ficha"
                titulo={l?.name ?? 'Lista'}
                eyebrow={`Lista · ${evento?.name ?? ''}`}
                subtitulo={l === undefined ? '' : l.capacity == null ? `${String(l.guests_count)} apuntados · sin límite propio` : `${String(l.guests_count)} de ${String(l.capacity)} apuntados`}
                chip={chip?.[0]}
                chipEstado={chip?.[1]}
                icono="list-checks"
                accion={accion}
                accionIcono={pestana === 0 ? 'user-plus' : 'check'}
                ocupado={ocupado && ventana === null}
                onAccion={pestana === 0 ? () => { ctx.abrir({ tipo: 'apuntar', lista: listaId }); } : guardar}
            >
                <div className="rv-ca-cuerpo">
                    <Botonera etiqueta="Partes de la lista" items={[{ texto: 'Invitados', icono: 'users' }, { texto: 'Ajustes', icono: 'settings' }, { texto: 'RRPP', icono: 'user' }]} valor={pestana} onCambio={setPestana} />
                    {l === undefined || f === null ? (
                        <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                    ) : (
                        <>
                            {pestana === 0 && (
                                <>
                                    {l.capacity != null ? (
                                        <div className="rv-ls-barrita rv-ls-barrita--ancha">
                                            <span className="rv-ls-barrita__cab"><span>Aforo de la lista</span><strong>{apuntadosLista(l)}</strong></span>
                                            <span className="rv-ls-barrita__fondo"><span style={{ width: `${String(Math.min(100, Math.round((l.guests_count / Math.max(1, l.capacity)) * 100)))}%`, background: colorDe(l.color) }} /></span>
                                            <small>{l.capacity - l.guests_count > 0 ? `Quedan ${String(l.capacity - l.guests_count)} plazas · además manda el aforo del evento` : 'Lista llena'}</small>
                                        </div>
                                    ) : (
                                        <Caja icono="users">{l.guests_count} apuntados. Sin aforo propio: manda el del evento.</Caja>
                                    )}
                                    {l.status === 'closed' && <Caja icono="lock">La lista está cerrada: nadie más se puede apuntar, tampoco por los enlaces. Los apuntados siguen con su pase.</Caja>}
                                    <Bloque titulo="Invitados" derecha={invitados.datos !== null && invitados.datos.data.length > 0 ? `${String(invitados.datos.data.length)} · primera página` : undefined}>
                                        {invitados.datos === null ? (
                                            <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                                        ) : invitados.datos.data.length === 0 ? (
                                            <Vacia icono="users" titulo="Nadie apuntado todavía" texto={apuntar ? 'Apunta a alguien con su Instagram o su email.' : ''} />
                                        ) : (
                                            invitados.datos.data.map((g) => {
                                                const e = estadoInvitado(g, terminado);
                                                const quien = g.member_id == null ? 'La casa' : nombreRrpp(ctx.rrpp.get(g.member_id));
                                                return (
                                                    <Fila
                                                        key={g.id}
                                                        inicial
                                                        titulo={g.display_name}
                                                        linea={`${contactoInvitado(g)[0]} · ${quien}${g.companions > 0 ? ` · +${String(g.companions)}` : ''}${e.chip === null ? ` · ${e.texto.toLocaleLowerCase('es')}` : ''}`}
                                                        chip={e.chip === null ? null : [e.texto, e.chip]}
                                                        apagada={g.status !== 'active'}
                                                        onAbrir={() => { ctx.abrir({ tipo: 'invitado', id: g.id }); }}
                                                    />
                                                );
                                            })
                                        )}
                                    </Bloque>
                                </>
                            )}
                            {pestana === 1 &&
                                (gestionar ? (
                                    <>
                                        <FormLista f={f} cambiar={(c) => { setF({ ...f, ...c }); }} errores={errores} plantilla={false} whatsapp={ctx.whatsapp} movil={movil} onCanalesDeEnvio={() => { ctx.props.navegar(RUTA_CANALES_DE_ENVIO); }} />
                                        <Bloque titulo="Estado de la lista">
                                            {envio.pendiente !== null && (
                                                <div className="rv-ca-deshacer">
                                                    <span>{envio.pendiente.texto}</span>
                                                    <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                                                </div>
                                            )}
                                            <Botones nota={l.status === 'open' ? 'Cerrarla: nadie más se apunta. Cancelarla: se retira a todos sus invitados y se anulan sus pases.' : 'Reabrirla pide un cierre nuevo, antes del final del evento.'}>
                                                {l.status === 'open' && envio.pendiente === null && (
                                                    <Boton tipo="secundario" icono="lock" onClick={cerrar}>Cerrar la lista</Boton>
                                                )}
                                                {l.status === 'closed' && (
                                                    <Boton tipo="secundario" icono="lock-open" onClick={() => { setCierre(''); setProbado(false); setVentana('reabrir'); }}>Reabrir</Boton>
                                                )}
                                                <Boton tipo="destructivo" icono="ban" onClick={() => { setMotivo(''); setProbado(false); setVentana('cancelar'); }}>Cancelar la lista</Boton>
                                            </Botones>
                                        </Bloque>
                                    </>
                                ) : (
                                    <Bloque>
                                        <Dato icono="users" k="Aforo propio" v={l.capacity == null ? 'Sin límite propio' : `${String(l.capacity)} personas`} />
                                        <Dato icono="user-plus" k="Acompañantes" v={(l.max_companions ?? 0) > 0 ? `Hasta ${String(l.max_companions)}` : 'Ninguno'} />
                                        <Dato icono="clock" k="Hora límite de entrada" v={l.door_cutoff_at == null ? 'Sin hora límite' : diaHora(l.door_cutoff_at)} />
                                        <Dato icono="lock" k="Cierre de la lista" v={l.closes_at == null ? 'Al empezar el evento' : diaHora(l.closes_at)} />
                                        <Dato icono="euro" k="En puerta" v={enPuerta(l.door_price, true)} />
                                        <Dato icono="instagram" k="Alta pública" v={l.public_signup === true ? (l.web_form_delivery === 'whatsapp' ? 'Instagram y web (el formulario envía por WhatsApp)' : 'Instagram y web') : 'No: solo el equipo y sus RRPP'} />
                                        <Dato icono="user-plus" k="Acompañantes por invitado" v={acompanantes(l.max_companions)} />
                                        {!permisos.puede(PERMISOS.listasGestionar) && <Caja icono="eye">Los ajustes de la lista los cambia el propietario o un administrador.</Caja>}
                                    </Bloque>
                                ))}
                            {pestana === 2 && (
                                <>
                                    <Bloque titulo="RRPP con cupo en esta lista">
                                        {conCupo.length === 0 ? (
                                            <Vacia icono="user" titulo="Ningún RRPP tiene cupo aquí" texto="Asígnalos desde RRPP › En el evento." />
                                        ) : (
                                            conCupo.map((a) => {
                                                const c = a.lists.find((x) => x.guest_list_id === listaId);
                                                return (
                                                    <Fila
                                                        key={a.id}
                                                        inicial
                                                        titulo={nombreRrpp(ctx.rrpp.get(a.member_id))}
                                                        linea={c?.quota === 0 ? 'No puede apuntar en esta lista' : c?.quota == null ? 'Sin límite en esta lista' : `Cupo de ${String(c.quota)} personas`}
                                                        chip={a.signup_list_id === listaId ? ['Su enlace apunta aquí', 'neutral'] : null}
                                                        apagada={c?.quota === 0}
                                                    />
                                                );
                                            })
                                        )}
                                    </Bloque>
                                    <Caja icono="info">Los cupos de cada RRPP se cambian en RRPP › En el evento. En el evento: {conCupo.map((a) => `${nombreRrpp(ctx.rrpp.get(a.member_id))} ${cupoTexto(a.event_quota, a.event_used)}`).join(' · ') || 'nadie'}.</Caja>
                                </>
                            )}
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={ventana !== null}
                titulo={ventana === 'cancelar' ? `Cancelar «${l?.name ?? ''}»` : `Reabrir «${l?.name ?? ''}»`}
                texto={
                    ventana === 'cancelar'
                        ? (l?.guests_count ?? 0) > 0
                            ? `Tiene ${String(l?.guests_count ?? 0)} apuntados. Se retira a cada uno, se anula su pase y su enlace personal y les avisamos.`
                            : 'No tiene a nadie apuntado.'
                        : 'Elige hasta cuándo se puede apuntar la gente. Tiene que ser antes del final del evento.'
                }
                cancelar="Volver"
                accion={ventana === 'cancelar' ? ((l?.guests_count ?? 0) > 0 ? 'Cancelar y avisar' : 'Cancelar la lista') : 'Reabrir'}
                accionIcono={ventana === 'cancelar' ? 'ban' : 'lock-open'}
                ocupado={ocupado}
                ocupadoTexto={ventana === 'cancelar' ? 'Cancelando…' : 'Reabriendo…'}
                movil={movil}
                onAccion={confirmar}
                onCerrar={() => { if (!ocupado) { setVentana(null); } }}
            >
                {ventana === 'cancelar' ? (
                    <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} max={300} error={probado && motivo.trim() === '' ? 'Escribe el motivo.' : null} ayuda="Queda anotado. No se envía tal cual." />
                ) : (
                    <FechaHora etiqueta="Nuevo cierre" valor={cierre} onChange={setCierre} error={probado && cierre === '' ? 'Elige la fecha y la hora.' : null} max={aLocal(evento?.ends_at)} />
                )}
            </Ventana>
        </>
    );
}
